import { type ReactNode } from 'react';

export interface AlertProps {
  variant?: 'warning' | 'danger' | 'success' | 'info';
  title?: string;
  children: ReactNode;
  className?: string;
}

export function Alert({
  variant = 'info',
  title,
  children,
  className = '',
}: AlertProps) {
  const variantStyles = {
    warning: {
      container: 'border-feedback-warning/40 bg-feedback-warning/10 text-brand-offWhite',
      iconColor: 'text-feedback-warning',
      icon: (
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
        />
      ),
    },
    danger: {
      container: 'border-feedback-danger/40 bg-feedback-danger/10 text-brand-offWhite',
      iconColor: 'text-feedback-danger',
      icon: (
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      ),
    },
    success: {
      container: 'border-feedback-success/40 bg-feedback-success/10 text-brand-offWhite',
      iconColor: 'text-feedback-success',
      icon: (
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      ),
    },
    info: {
      container: 'border-brand-orange/40 bg-brand-orange/10 text-brand-offWhite',
      iconColor: 'text-brand-orange',
      icon: (
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      ),
    },
  };

  const current = variantStyles[variant];

  return (
    <div
      role="alert"
      className={`flex items-start gap-3 rounded-2xl border p-4 text-left shadow-sm ${current.container} ${className}`}
    >
      <div className={`mt-0.5 shrink-0 ${current.iconColor}`}>
        <svg
          className="h-5 w-5"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
          aria-hidden="true"
        >
          {current.icon}
        </svg>
      </div>

      <div className="flex-1 text-xs sm:text-sm">
        {title && <h4 className="font-bold text-white mb-1">{title}</h4>}
        <div className="leading-relaxed opacity-95">{children}</div>
      </div>
    </div>
  );
}
